import { type Command, InvalidOptionArgumentError } from 'commander';
import { z } from 'zod';
import { isGlab } from '@gitlab-org/tui';
import type { DuoCommand, Example, OptionGroup } from '../duo_command';
import type { BackendConfigAdapter } from '../../backend/backend_config_adapter';
import { initDi } from '../../di';
import { parse } from '../../parse';
import { exportCommandOptionDefs } from '../../command_option_defs';
import { registerOptionsOnCommand, zodShapeFromDefMap } from '../../option_def';
import { getHeadlessEnvInfo } from '../../utils/environment';
import { CredentialProvider } from '../../utils/credential_provider';
import type { ExitHandler } from '../../utils/exit';
import { UsageError } from '../../utils/usage_error';
import { ExportController } from './export_controller';

export const gitlabExportExamples: Example[] = [
  {
    title: 'Export a session to the default location',
    exampleCommand: 'duo export --existing-session-id 4122303',
  },
  {
    title: 'Export to a specific file',
    exampleCommand: 'duo export --existing-session-id 4122303 --output ./session.json',
  },
  {
    title: 'Pipe the bundle to another tool',
    exampleCommand: 'duo export --existing-session-id 4122303 --output-format json | jq .summary',
  },
];

export const gitlabExportSynopsis =
  'Writes a portable JSON bundle of a session: its transcript, plus a derived summary of goals, files read and changed, commands run, and unresolved errors. ' +
  'Works without a running session; it reads the session from GitLab by ID. ' +
  'The file and stdout are independent: `--output-format json` on its own writes no file, so it can be piped.';

export const gitlabExportNotes =
  'The exported session replaces all Duo CLI current-working-directory paths with a `<gl-duo-root>` token, and runs every string through the secret redactor, ' +
  'but it is not a sanitised artifact: host detail that is not a path survives inside tool output, e.g., an `ls -la` owner column, ' +
  'a stack trace, an `env` dump. Review a bundle before attaching it anywhere public.\n\n' +
  "The export reads the session's latest saved checkpoint, so a bundle taken during an in-flight turn may not include it yet.\n\n" +
  'Paths are anchored against the checkout you run the command in, which is also what `workspace.rootName` records. ' +
  "Run it from the directory the session ran in: exporting from elsewhere leaves that session's absolute paths unanchored in the bundle.";

export interface ExportCommandOptions<TBackendOpts> {
  adapter: BackendConfigAdapter<TBackendOpts>;
  version: string;
  exitHandler: ExitHandler;
  /** Doc-only — examples for the auto-generated reference. */
  examples?: Example[];
  /** Doc-only — synopsis for the auto-generated reference. */
  synopsis?: string;
  /** Doc-only — notes for the auto-generated reference. */
  notes?: string;
  /** Doc-only — option groups rendered for this command in the reference. */
  optionGroups?: OptionGroup[];
}

const exportOptionsSchema = z.object(zodShapeFromDefMap(exportCommandOptionDefs));

export class ExportCommand<TBackendOpts> implements DuoCommand {
  readonly name = 'export';

  readonly description = 'Export a session as a portable JSON bundle.';

  readonly examples?: Example[];

  readonly synopsis?: string;

  readonly notes?: string;

  readonly optionGroups?: OptionGroup[];

  readonly #adapter: BackendConfigAdapter<TBackendOpts>;

  readonly #version: string;

  readonly #exitHandler: ExitHandler;

  constructor(options: ExportCommandOptions<TBackendOpts>) {
    this.#adapter = options.adapter;
    this.#version = options.version;
    this.#exitHandler = options.exitHandler;
    this.examples = options.examples;
    this.synopsis = options.synopsis;
    this.notes = options.notes;
    this.optionGroups = options.optionGroups;
  }

  register(node: Command): void {
    registerOptionsOnCommand(node, exportCommandOptionDefs);
    this.#adapter.registerOptions(node);

    node.action(async () => {
      const allOpts = node.optsWithGlobals();
      // `tui` like AcpCommand: a third name would mean widening di.ts's checks.
      const parsed = parse(allOpts, 'tui');

      // Not commander's `mandatory` — see exportCommandOptionDefs.
      if (!allOpts.existingSessionId) {
        throw new UsageError(
          'Specify the session to export with --existing-session-id <sessionId>. `duo log list` shows the log files for recent sessions.',
        );
      }

      const exportOptions = exportOptionsSchema.parse(allOpts);
      const backendOpts = this.#adapter.parseOptions(allOpts);

      // stdout carries the bundle or the written path, so logs go to stderr.
      const envInfo = await getHeadlessEnvInfo();

      const { container, configurationController } = await initDi(
        { logDestination: 'stderr' },
        parsed,
        this.#version,
        envInfo,
        this.#exitHandler,
        this.#adapter,
        backendOpts,
      );

      const credentialProvider = container.getRequiredService(CredentialProvider);
      const { token, source } = await credentialProvider.getCredentials();

      if (configurationController.isMissingConfiguration() && !token) {
        if (isGlab()) {
          const hint =
            source.type === 'none' && source.glabHint
              ? source.glabHint
              : "Run 'glab auth login' to authenticate.";
          throw new Error(`No credentials found. ${hint}`);
        }
        throw new InvalidOptionArgumentError(
          '--gitlab-auth-token / GITLAB_AUTH_TOKEN option is required.',
        );
      }

      const controller = container.getRequiredService(ExportController);
      await controller.execute({
        sessionId: exportOptions.existingSessionId,
        cwd: parsed.cwd,
        output: exportOptions.output,
        outputFormat: exportOptions.outputFormat,
      });
    });
  }
}
